import { useEffect, useRef, useState, useMemo } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Trash2,
  ChevronDown, Package, Sparkles, Info, Download, Undo2, Square,
  ImageIcon, X, Search, Plus, Link2, TrendingUp, FileUp, CheckCheck,
  XCircle, ChevronUp, Pencil, Check, ArrowRightLeft, DollarSign, Tag,
  Clock, RefreshCw, ScrollText,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/* ── History helpers ───────────────────────────────────────────────────── */
const HIST_STATUS = {
  pending:     { label: "بانتظار التنفيذ", color: "text-[var(--text-muted)]",    bg: "bg-[var(--bg-card-hover)]"    },
  running:     { label: "جارٍ...",          color: "text-blue-500",   bg: "bg-blue-50"     },
  success:     { label: "اكتمل",            color: "text-emerald-600", bg: "bg-emerald-50" },
  partial:     { label: "جزئي",             color: "text-amber-600",  bg: "bg-amber-50"    },
  failed:      { label: "فشل",              color: "text-red-500",    bg: "bg-red-50"      },
  rolled_back: { label: "تراجع",            color: "text-[var(--text-muted)]",   bg: "bg-[var(--bg-card-hover)]"    },
};
function HistoryBadge({ status }) {
  const s = HIST_STATUS[status] || HIST_STATUS.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.bg} ${s.color}`}>
      {status === "success" && <CheckCircle2 className="w-3 h-3" />}
      {status === "failed"  && <XCircle className="w-3 h-3" />}
      {status === "running" && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === "partial" && <AlertTriangle className="w-3 h-3" />}
      {s.label}
    </span>
  );
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Intl.DateTimeFormat("ar-SA", { year:"numeric", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit", hour12:true }).format(new Date(iso)); }
  catch { return iso; }
}

/* ── History stat card ─────────────────────────────────────────────────── */
function HistoryStat({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const p = {
    default: { card: "bg-[var(--bg-card)] border-[var(--border-color)]",       icon: "bg-[var(--bg-card-hover)] text-[var(--text-muted)]",       val: "text-gray-800" },
    primary: { card: "bg-[var(--bg-card)] border-[#0044FF]/20",   icon: "bg-[#0044FF]/10 text-[#0044FF]",  val: "text-[#0044FF]" },
    success: { card: "bg-[var(--bg-card)] border-emerald-200",    icon: "bg-emerald-50 text-emerald-600",  val: "text-emerald-600" },
    error:   { card: "bg-[var(--bg-card)] border-red-200",        icon: "bg-red-50 text-red-500",          val: "text-red-500" },
    amber:   { card: "bg-[var(--bg-card)] border-amber-200",      icon: "bg-amber-50 text-amber-600",      val: "text-amber-600" },
  }[tone];
  return (
    <div data-testid={testid} className={`border rounded-xl p-5 ${p.card} flex items-center gap-4`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${p.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className={`text-2xl font-bold font-mono ${p.val}`}>{value}</div>
        <div className="text-xs text-[var(--text-muted)] mt-0.5">{label}</div>
        {sub && <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

/* ── Session stat card ─────────────────────────────────────────────────── */
function SessionStat({ label, value, tone = "default", testid }) {
  const toneClass = {
    default: "text-gray-800",
    success: "text-emerald-600",
    error:   "text-red-500",
    warning: "text-amber-600",
    primary: "text-[#0044FF]",
  }[tone];
  return (
    <div data-testid={testid} className="text-center">
      <div className={`text-2xl font-bold font-mono ${toneClass}`}>{value}</div>
      <div className="text-[10px] text-[var(--text-muted)] mt-0.5 tracking-wide">{label}</div>
    </div>
  );
}

/* ── Inline editable cell ───────────────────────────────────────────────── */
function InlineCell({ value, onStart, editing, editVal, onChange, onCommit, onKeyDown, inputType = "text", placeholder = "" }) {
  const ref = useRef(null);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);
  if (editing) {
    return (
      <input
        ref={ref}
        type={inputType}
        value={editVal}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full px-1.5 py-0.5 text-xs border border-[#0044FF]/40 rounded bg-blue-50 focus:outline-none font-mono"
        style={{ minWidth: 60 }}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={onStart}
      title="اضغط للتعديل"
      className="group/cell flex items-center gap-1 hover:text-[#0044FF] transition-colors text-start w-full"
    >
      <span className={`font-mono text-xs ${value ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)] italic"}`}>
        {value || "—"}
      </span>
      <Pencil className="w-2.5 h-2.5 opacity-0 group-hover/cell:opacity-60 shrink-0 text-[#0044FF]" />
    </button>
  );
}

export default function Import() {
  const inputRef        = useRef(null);
  const [uploading,     setUploading]      = useState(false);
  const [session,       setSession]        = useState(null);
  const [dragOver,      setDragOver]       = useState(false);
  const [expanded,      setExpanded]       = useState({});
  const [fetchingImages, setFetchingImages] = useState(false);
  const [schemaOpen,    setSchemaOpen]     = useState(false);
  const [mainTab,       setMainTab]        = useState("upload");
  const [histLoading,   setHistLoading]    = useState(false);

  /* history stats */
  const [allSessions,   setAllSessions]    = useState([]);

  /* image picker popup */
  const [imgPickerState, setImgPickerState] = useState({
    open: false, tempId: null, productName: "", query: "",
    results: [], selected: [], loading: false,
  });

  /* paste URLs */
  const [pasteUrls,      setPasteUrls]      = useState({});
  const [addingUrls,     setAddingUrls]     = useState({});
  const [showPasteInput, setShowPasteInput] = useState({});

  /* inline editing */
  const [editingCell, setEditingCell] = useState(null);
  const [editVal,     setEditVal]     = useState("");
  const [addingVariant, setAddingVariant] = useState({});
  const [newVariant, setNewVariant]   = useState({});

  /* move variant modal */
  const [moveState, setMoveState] = useState(null); // {variantTempId, variantLabel, srcProductId}

  /* product-level price editing */
  const [editingProductPrice, setEditingProductPrice] = useState(null); // product temp_id
  const [productPriceVal, setProductPriceVal] = useState("");

  /* product search/filter */
  const [productSearch, setProductSearch] = useState("");

  /* ── filtered products (client-side search) ── */
  const filteredProducts = useMemo(() => {
    const prods = session?.products || [];
    if (!productSearch.trim()) return prods;
    const q = productSearch.trim().toLowerCase();
    return prods.filter((p) => {
      const nameMatch   = (p.name    || "").toLowerCase().includes(q);
      const nameArMatch = (p.name_ar || "").toLowerCase().includes(q);
      const mpnMatch    = (p.mpn     || "").toLowerCase().includes(q);
      const skuMatch    = p.variants?.some((v) => (v.sku || "").toLowerCase().includes(q));
      return nameMatch || nameArMatch || mpnMatch || skuMatch;
    });
  }, [session?.products, productSearch]);

  /* ── aggregate history stats ── */
  const historyStats = useMemo(() => {
    const total    = allSessions.length;
    const created  = allSessions.reduce((s, x) => s + (x.progress?.created || 0), 0);
    const failed   = allSessions.reduce((s, x) => s + (x.progress?.failed || 0), 0);
    const rate     = created + failed > 0 ? Math.round((created / (created + failed)) * 100) : 0;
    return { total, created, failed, rate };
  }, [allSessions]);

  /* ── Load last session + all sessions for stats ── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/import/xlsx/sessions", { params: { limit: 50 } });
        if (data?.length) {
          setAllSessions(data);
          const r = await api.get(`/import/xlsx/session/${data[0].id}`);
          setSession(r.data);
        }
      } catch {}
    })();
  }, []);

  /* Poll running session */
  useEffect(() => {
    if (!session?.id || session.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/import/xlsx/session/${session.id}`);
        setSession(data);
      } catch {}
    }, 1500);
    return () => clearInterval(id);
  }, [session?.id, session?.status]);

  /* ── Actions ── */
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/import/xlsx/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s = { id: data.session_id, ...data };
      setSession(s);
      setAllSessions((prev) => [s, ...prev]);
      const next = {};
      (s.products || []).forEach((p) => { next[p.temp_id] = true; });
      setExpanded(next);
      toast.success("تم تحليل الملف بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const removeProduct = async (temp_id) => {
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, { remove_product_ids: [temp_id] });
      setSession((s) => ({ ...s, ...data }));
      toast.success("تم حذف المنتج");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeVariant = async (temp_id) => {
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, { remove_variant_ids: [temp_id] });
      setSession((s) => ({ ...s, ...data }));
      toast.success("تم حذف المقاس");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearSession = () => {
    // Only clears the active preview — session stays in history
    setSession(null);
    toast.success("تم مسح الجلسة الحالية، يمكنك رفع ملف جديد");
  };

  const execute = async () => {
    try {
      await api.post(`/import/xlsx/execute/${session.id}`);
      toast.success("بدأ الإنشاء في سلة");
      const { data } = await api.get(`/import/xlsx/session/${session.id}`);
      setSession(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rollback = async () => {
    try {
      const { data } = await api.post(`/import/xlsx/rollback/${session.id}`);
      toast.success(`تم حذف ${data.deleted} منتج من سلة`);
      const r = await api.get(`/import/xlsx/session/${session.id}`);
      setSession(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelRun = async () => {
    try { await api.post(`/sync/cancel`); toast.success("تم طلب الإيقاف"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSession = async (id) => {
    try {
      await api.delete(`/import/xlsx/session/${id}`);
      setAllSessions((prev) => prev.filter((s) => s.id !== id));
      toast.success("تم حذف الجلسة");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshHistory = async () => {
    setHistLoading(true);
    try {
      const { data } = await api.get("/import/xlsx/sessions", { params: { limit: 100 } });
      setAllSessions(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistLoading(false); }
  };

  const downloadTemplate = () => window.open(`${API_BASE}/import/xlsx/template`, "_blank");

  const fetchImages = async () => {
    setFetchingImages(true);
    try {
      const { data } = await api.post(`/import/xlsx/session/${session.id}/fetch-images`);
      setSession((s) => ({ ...s, products: data.products }));
      toast.success(`تم جلب الصور لـ ${data.fetched} منتج`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setFetchingImages(false); }
  };

  const removeImage = async (productTempId, imageUrl) => {
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        product_image_removes: { [productTempId]: [imageUrl] },
      });
      setSession((s) => ({ ...s, ...data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveImage = async (tempId, url) => {
    const isSkip = url === "__skip__";
    const approvalUrl = isSkip ? null : url;
    setSession((prev) => ({
      ...prev,
      products: (prev.products || []).map((p) =>
        p.temp_id === tempId ? { ...p, approved_image_url: approvalUrl, images_skip_upload: isSkip } : p
      ),
    }));
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        product_image_approvals: { [tempId]: approvalUrl },
      });
      setSession((s) => ({ ...s, ...data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitPasteUrls = async (tempId) => {
    const raw = (pasteUrls[tempId] || "").trim();
    if (!raw) return;
    const urls = raw.split(/\n|,/).map((u) => u.trim()).filter((u) => u.startsWith("http"));
    if (!urls.length) { toast.error("أدخل روابط صالحة تبدأ بـ http"); return; }
    setAddingUrls((prev) => ({ ...prev, [tempId]: true }));
    try {
      const { data } = await api.post(
        `/import/xlsx/session/${session.id}/add-urls/${tempId}`,
        { urls: urls.slice(0, 5) }
      );
      setSession((prev) => ({
        ...prev,
        products: (prev.products || []).map((p) =>
          p.temp_id === tempId ? { ...p, images: data.images } : p
        ),
      }));
      toast.success(`أُضيفت ${data.added} صورة`);
      setPasteUrls((prev) => ({ ...prev, [tempId]: "" }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingUrls((prev) => ({ ...prev, [tempId]: false })); }
  };

  const downloadArranged = async () => {
    try {
      const resp = await api.get(`/import/xlsx/session/${session.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `arranged-${session.filename || "file"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openImagePicker = (tempId, productName) => {
    setImgPickerState({ open: true, tempId, productName, query: "", results: [], selected: [], loading: false });
  };

  const runPickerSearch = async (query) => {
    if (!query.trim()) return;
    setImgPickerState((s) => ({ ...s, loading: true, results: [], selected: [] }));
    try {
      const { data } = await api.post(`/import/xlsx/session/${session.id}/search-image-preview`, { query: query.trim() });
      setImgPickerState((s) => ({ ...s, results: data.images || [], loading: false }));
    } catch (e) {
      toast.error(formatApiError(e));
      setImgPickerState((s) => ({ ...s, loading: false }));
    }
  };

  const togglePickerSelect = (img) => {
    setImgPickerState((s) => {
      const already = s.selected.some((x) => x.url === img.url);
      if (already) return { ...s, selected: s.selected.filter((x) => x.url !== img.url) };
      if (s.selected.length >= 10) { toast.info("الحد الأقصى 10 صور"); return s; }
      return { ...s, selected: [...s.selected, img] };
    });
  };

  const confirmPickerSelection = async () => {
    const { tempId, selected } = imgPickerState;
    if (!selected.length) return;
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        product_image_adds: { [tempId]: selected },
      });
      setSession((s) => ({ ...s, ...data }));
      toast.success(`أُضيفت ${selected.length} صورة للمنتج`);
      setImgPickerState((s) => ({ ...s, open: false }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  /* ── Inline editing helpers ── */
  const startEdit = (type, temp_id, field, currentVal) => {
    setEditingCell({ type, temp_id, field });
    setEditVal(currentVal ?? "");
  };
  const cancelEdit = () => { setEditingCell(null); setEditVal(""); };

  const commitEdit = async () => {
    if (!editingCell) return;
    const { type, temp_id, field } = editingCell;
    cancelEdit();
    try {
      if (type === "product_name") {
        const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
          product_name_updates: { [temp_id]: editVal },
        });
        setSession((s) => ({ ...s, ...data }));
        toast.success("تم تحديث اسم المنتج");
      } else if (type === "variant") {
        const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
          variant_updates: { [temp_id]: { [field]: editVal } },
        });
        setSession((s) => ({ ...s, ...data }));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") cancelEdit();
  };

  /* ── Add variant helpers ── */
  const startAddVariant = (pid) => {
    setAddingVariant((v) => ({ ...v, [pid]: true }));
    setNewVariant((v) => ({ ...v, [pid]: { sku: "", size: "", qty: "", price: "" } }));
  };

  const commitAddVariant = async (pid) => {
    const nv = newVariant[pid] || {};
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        add_variants: { [pid]: [nv] },
      });
      setSession((s) => ({ ...s, ...data }));
      toast.success("تمت إضافة المقاس");
      setAddingVariant((v) => ({ ...v, [pid]: false }));
      setNewVariant((v) => ({ ...v, [pid]: {} }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ── Move variant ── */
  const openMoveModal = (v, srcProductId) => {
    setMoveState({ variantTempId: v.temp_id, variantLabel: `${v.size || "—"} (${v.sku || "—"})`, srcProductId });
  };

  const confirmMove = async (destProductId) => {
    const { variantTempId } = moveState;
    setMoveState(null);
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        move_variants: { [variantTempId]: destProductId },
      });
      setSession((s) => ({ ...s, ...data }));
      toast.success("تم نقل المقاس بنجاح");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ── Price mode ── */
  const togglePriceMode = async (pid, currentMode) => {
    const newMode = currentMode === "product" ? "variant" : "product";
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        product_price_mode_updates: { [pid]: newMode },
      });
      setSession((s) => ({ ...s, ...data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const commitProductPrice = async (pid) => {
    const val = productPriceVal;
    setEditingProductPrice(null);
    setProductPriceVal("");
    try {
      const { data } = await api.patch(`/import/xlsx/session/${session.id}`, {
        product_price_updates: { [pid]: val },
      });
      setSession((s) => ({ ...s, ...data }));
      toast.success("تم تحديث سعر المنتج");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ── Status config ── */
  const statusConfig = {
    success:     { label: "اكتمل بنجاح",        color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
    failed:      { label: "فشل",                 color: "text-red-500",     bg: "bg-red-50 border-red-200" },
    partial:     { label: "جزئي",                color: "text-amber-600",   bg: "bg-amber-50 border-amber-200" },
    rolled_back: { label: "تم التراجع",          color: "text-[var(--text-muted)]",    bg: "bg-[var(--bg-card-hover)] border-[var(--border-color)]" },
    pending:     { label: "بانتظار التنفيذ",     color: "text-[var(--text-muted)]",    bg: "bg-[var(--bg-card-hover)] border-[var(--border-muted)]" },
    running:     { label: "جارٍ الإنشاء...",     color: "text-[#0044FF]",   bg: "bg-blue-50 border-blue-200" },
  };

  /* ───────────────────────────────────────────────────── render ── */
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">

        {/* ══ Header ══════════════════════════════════════════════════════ */}
        <header className="fade-up flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">استيراد Excel</p>
            <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-gray-900 mt-1">
              استيراد المنتجات والمقاسات
            </h1>
            <p className="text-[var(--text-muted)] mt-2 text-sm max-w-2xl">
              ارفع ملف <span className="font-mono text-[var(--text-secondary)] bg-[var(--bg-card-hover)] px-1.5 py-0.5 rounded">.xlsx</span> أو{" "}
              <span className="font-mono text-[var(--text-secondary)] bg-[var(--bg-card-hover)] px-1.5 py-0.5 rounded">.csv</span> —
              النظام يحلّله ويعرض معاينة قابلة للتعديل قبل الإنشاء في سلة.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            data-testid="import-template-button"
            className="flex items-center gap-2 px-4 py-2.5 border border-[#004EEB] text-[#004EEB] rounded-xl text-sm font-medium hover:bg-[#004EEB]/5 transition-colors"
          >
            <Download className="w-4 h-4 rtl-flip" />
            تحميل نموذج جاهز
          </button>
        </header>

        {/* ══ Tab Switch ═══════════════════════════════════════════════════ */}
        <div className="flex gap-1 bg-[var(--bg-card-hover)] p-1 rounded-xl w-fit">
          <button
            data-testid="import-tab-upload"
            onClick={() => setMainTab("upload")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mainTab === "upload"
                ? "bg-[var(--bg-card)] text-[#004EEB] shadow-sm"
                : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            }`}
          >
            <Upload className="w-4 h-4" />
            استيراد جديد
          </button>
          <button
            data-testid="import-tab-history"
            onClick={() => { setMainTab("history"); refreshHistory(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mainTab === "history"
                ? "bg-[var(--bg-card)] text-[#004EEB] shadow-sm"
                : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            }`}
          >
            <ScrollText className="w-4 h-4" />
            سجل الاستيرادات
            {allSessions.length > 0 && (
              <span className="bg-[#004EEB]/10 text-[#004EEB] text-xs font-bold px-1.5 py-0.5 rounded-full">
                {allSessions.length}
              </span>
            )}
          </button>
        </div>

        {/* ══ History Tab ══════════════════════════════════════════════════ */}
        {mainTab === "history" && (
          <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]/50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--text-secondary)] flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--text-muted)]" />
                الجلسات ({allSessions.length.toLocaleString("en-US")})
              </h2>
              <button onClick={refreshHistory} disabled={histLoading}
                className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-color)] rounded-lg text-xs hover:border-gray-400 transition-colors disabled:opacity-50">
                {histLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                تحديث
              </button>
            </div>

            {histLoading && (
              <div className="py-12 flex flex-col items-center gap-3 text-[var(--text-muted)]">
                <Loader2 className="w-6 h-6 animate-spin text-[#004EEB]" />
                <span className="text-sm">جارٍ التحميل...</span>
              </div>
            )}

            {!histLoading && allSessions.length === 0 && (
              <div className="py-14 flex flex-col items-center gap-4 text-[var(--text-muted)]">
                <div className="w-14 h-14 rounded-2xl bg-[var(--bg-card-hover)] flex items-center justify-center">
                  <FileSpreadsheet className="w-7 h-7 text-[var(--text-muted)]" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-[var(--text-muted)]">لا توجد جلسات بعد</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">ارفع أول ملف Excel من تبويب «استيراد جديد»</p>
                </div>
                <button onClick={() => setMainTab("upload")}
                  className="flex items-center gap-2 px-4 py-2 bg-[#004EEB] text-white rounded-xl text-sm font-medium hover:bg-[#0033CC] transition-colors">
                  <Upload className="w-4 h-4 rtl-flip" />
                  استيراد الآن
                </button>
              </div>
            )}

            {!histLoading && allSessions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="history-table">
                  <thead>
                    <tr className="border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]/80">
                      <th className="text-start px-6 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الملف</th>
                      <th className="text-start px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">تاريخ الرفع</th>
                      <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">المنتجات</th>
                      <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">أُنشئ</th>
                      <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">فشل</th>
                      <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الحالة</th>
                      <th className="text-center px-6 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">حذف</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSessions.map((s) => (
                      <tr key={s.id} className="border-b border-[var(--border-muted)] hover:bg-[var(--bg-card-hover)]/60 transition-colors"
                        data-testid={`history-row-${s.id}`}>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                              <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-800 truncate max-w-[200px]">{s.filename || "—"}</div>
                              <div className="text-[10px] text-[var(--text-muted)] font-mono">{s.id?.slice(0, 8)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-[var(--text-muted)] text-xs whitespace-nowrap">{fmtDate(s.uploaded_at)}</td>
                        <td className="px-4 py-3.5 text-center font-mono font-medium text-[var(--text-secondary)]">
                          {(s.stats?.products_count ?? 0).toLocaleString("en-US")}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="font-mono font-semibold text-emerald-600">
                            {(s.progress?.created ?? 0).toLocaleString("en-US")}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`font-mono font-semibold ${s.progress?.failed ? "text-red-500" : "text-[var(--text-muted)]"}`}>
                            {(s.progress?.failed ?? 0).toLocaleString("en-US")}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <HistoryBadge status={s.status || "pending"} />
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-1.5 border border-[var(--border-color)] rounded-lg hover:border-red-300 hover:text-red-500 transition-colors" title="حذف الجلسة">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent dir="rtl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>حذف الجلسة</AlertDialogTitle>
                                <AlertDialogDescription>
                                  سيتم حذف جلسة «{s.filename}» من السجل. لن يؤثر على المنتجات التي أُنشئت في سلة.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteSession(s.id)} className="bg-red-500 hover:bg-red-600">
                                  نعم، احذف
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ══ Upload Tab Content ═══════════════════════════════════════════ */}
        {mainTab === "upload" && (<>

        {/* ══ History Stats ════════════════════════════════════════════════ */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="import-history-stats">
          <HistoryStat
            testid="stat-total-files"
            icon={FileUp}
            label="إجمالي الملفات المرفوعة"
            value={historyStats.total.toLocaleString("en-US")}
            tone="primary"
          />
          <HistoryStat
            testid="stat-total-created"
            icon={CheckCheck}
            label="منتجات أُنشئت في سلة"
            value={historyStats.created.toLocaleString("en-US")}
            sub="عبر جميع الجلسات"
            tone="success"
          />
          <HistoryStat
            testid="stat-total-failed"
            icon={XCircle}
            label="منتجات فشل إنشاؤها"
            value={historyStats.failed.toLocaleString("en-US")}
            sub="عبر جميع الجلسات"
            tone="error"
          />
          <HistoryStat
            testid="stat-success-rate"
            icon={TrendingUp}
            label="معدل النجاح الإجمالي"
            value={`${historyStats.rate.toLocaleString("en-US")}%`}
            sub={historyStats.total > 0 ? `من ${historyStats.total} جلسة` : "لا توجد جلسات بعد"}
            tone="amber"
          />
        </section>

        {/* ══ Dropzone ═════════════════════════════════════════════════════ */}
        {!session && (
          <div
            data-testid="import-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer relative overflow-hidden rounded-2xl transition-all duration-300 ${
              dragOver
                ? "border-2 border-[#0044FF] shadow-[0_0_0_4px_rgba(0,68,255,0.12)]"
                : "border-2 border-dashed border-[var(--border-color)] hover:border-[#0044FF]/40 hover:shadow-lg"
            }`}
            style={{ background: "linear-gradient(135deg, #080F1A 0%, #0C1628 60%, #0a1020 100%)" }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.csv" hidden onChange={onFileInput} data-testid="import-file-input" />

            {/* Background grid */}
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />

            {/* Glow orb */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, #0044FF, transparent)" }} />

            <div className="relative z-10 py-16 px-8 flex flex-col items-center gap-5">
              {uploading ? (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-[#0044FF]/20 border border-[#0044FF]/30 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#0044FF]" />
                  </div>
                  <div className="text-center">
                    <div className="text-white font-semibold text-lg">جارٍ تحليل الملف...</div>
                    <div className="text-[var(--text-muted)] text-sm mt-1">النظام يقرأ المنتجات والمقاسات</div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 border ${
                    dragOver
                      ? "bg-[#0044FF]/30 border-[#0044FF]/60 scale-110"
                      : "bg-[var(--bg-card)]/5 border-white/10"
                  }`}>
                    <Upload className={`w-9 h-9 transition-colors ${dragOver ? "text-[#0044FF]" : "text-white/60"}`} />
                  </div>
                  <div className="text-center">
                    <div className="text-white text-xl font-bold">
                      {dragOver ? "أفلت الملف هنا" : "اسحب الملف هنا أو اضغط للاختيار"}
                    </div>
                    <div className="text-[var(--text-muted)] text-sm mt-2">
                      الصيغ المدعومة:{" "}
                      <span className="font-mono bg-[var(--bg-card)]/10 px-1.5 py-0.5 rounded text-white/80">.xlsx</span>
                      {" / "}
                      <span className="font-mono bg-[var(--bg-card)]/10 px-1.5 py-0.5 rounded text-white/80">.csv</span>
                      {" · "}الحد الأقصى <span className="text-white/80">10MB</span>
                    </div>
                  </div>

                  {/* Format badges */}
                  <div className="flex gap-3 mt-1">
                    <span className="flex items-center gap-1.5 text-xs bg-[var(--bg-card)]/10 text-white/70 border border-white/10 px-3 py-1.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      منتجات + مقاسات
                    </span>
                    <span className="flex items-center gap-1.5 text-xs bg-[var(--bg-card)]/10 text-white/70 border border-white/10 px-3 py-1.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      فاتورة مسطّحة ذكية
                    </span>
                    <span className="flex items-center gap-1.5 text-xs bg-[var(--bg-card)]/10 text-white/70 border border-white/10 px-3 py-1.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      بدون تنسيق مسبق
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ Schema hint (collapsible) ════════════════════════════════════ */}
        <div className="border border-[var(--border-color)] rounded-xl overflow-hidden bg-[var(--bg-card)]">
          <button
            onClick={() => setSchemaOpen((v) => !v)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-[var(--bg-card-hover)]/80 transition-colors"
            data-testid="schema-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0044FF]/10 flex items-center justify-center">
                <Info className="w-4 h-4 text-[#0044FF]" />
              </div>
              <div className="text-sm font-semibold text-gray-800">مرونة كاملة في صيغة الملف — النظام يرتّبه لك تلقائياً</div>
            </div>
            {schemaOpen
              ? <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" />
              : <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />
            }
          </button>
          {schemaOpen && (
            <div className="border-t border-[var(--border-muted)] px-5 py-5 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4">
                  <div className="text-xs font-bold text-[#0044FF] uppercase tracking-wider mb-2">الصيغة 1 — منظّمة</div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                    ملف يحوي عمود <span className="font-mono bg-[var(--bg-card)] px-1 rounded">النوع</span> بقيمتَي{" "}
                    <span className="font-mono bg-[var(--bg-card)] px-1 rounded">منتج</span> /{" "}
                    <span className="font-mono bg-[var(--bg-card)] px-1 rounded">مقاس</span> لتعريف كل صف صراحةً.
                  </p>
                </div>
                <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4">
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">الصيغة 2 — فاتورة مسطّحة (ذكي)</div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                    كل صف = SKU واحد. النظام يجمع الصفوف المشتركة في «رقم الموديل» ويستخرج المقاس من آخر الاسم تلقائياً
                    (مثل <span className="font-mono bg-[var(--bg-card)] px-1 rounded text-xs">FG. 42</span> أو{" "}
                    <span className="font-mono bg-[var(--bg-card)] px-1 rounded text-xs">TF. 41.1/3</span>).
                  </p>
                </div>
              </div>
              <div>
                <div className="text-xs text-[var(--text-muted)] font-semibold uppercase tracking-wider mb-2">الأعمدة المدعومة (بأي ترتيب)</div>
                <div className="flex flex-wrap gap-2">
                  {["النوع (اختياري)", "اسم المنتج", "SKU", "المقاس", "الكمية", "السعر / سعر البيع", "رقم الموديل", "يتطلب شحن ؟", "الوزن (kg)"].map((h) => (
                    <span key={h} className="font-mono text-[11px] bg-[var(--bg-card-hover)] text-[var(--text-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-1">{h}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ══ Session Preview ══════════════════════════════════════════════ */}
        {session && (
          <>
            {/* Session stats row */}
            <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-6 divide-x divide-x-reverse divide-gray-100">
              <SessionStat testid="import-stat-products" label="منتجات"          value={(session.stats?.products_count ?? 0).toLocaleString("en-US")} tone="primary" />
              <SessionStat testid="import-stat-variants" label="مقاسات"          value={(session.stats?.variants_count ?? 0).toLocaleString("en-US")} />
              <SessionStat testid="import-stat-errors"   label="أخطاء"           value={(session.stats?.error_count ?? 0).toLocaleString("en-US")}
                tone={session.stats?.error_count ? "error" : "success"} />
              <SessionStat testid="import-stat-rows"     label="إجمالي الصفوف"   value={(session.stats?.total_rows ?? 0).toLocaleString("en-US")} />
            </div>

            <section className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl overflow-hidden">
              {/* Section toolbar */}
              <div className="px-4 md:px-6 py-4 border-b border-[var(--border-muted)] flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-[var(--bg-card-hover)]/50">
                <div>
                  <h2 className="text-base font-semibold flex items-center gap-2 text-gray-800">
                    <FileSpreadsheet className="w-4 h-4 text-[#0044FF]" />
                    <span className="truncate max-w-[55vw] md:max-w-none">{session.filename}</span>
                  </h2>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">معاينة — البيانات محلية ولم تُكتب في سلة بعد.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={fetchImages}
                    disabled={fetchingImages}
                    data-testid="import-fetch-images-button"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium disabled:opacity-50 transition-colors"
                  >
                    {fetchingImages ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    {fetchingImages ? "جارٍ جلب الصور..." : "جلب الصور تلقائياً"}
                  </button>
                  <button
                    onClick={downloadArranged}
                    data-testid="import-download-arranged-button"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs border border-emerald-300 text-emerald-600 rounded-lg hover:bg-emerald-50 font-medium transition-colors"
                  >
                    <Download className="w-3.5 h-3.5 rtl-flip" />
                    تحميل الملف المرتّب
                  </button>
                  {session.status !== "running" && session.progress?.created > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="import-rollback-button" className="flex items-center gap-1.5 px-3 py-2 text-xs border border-amber-300 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors">
                          <Undo2 className="w-3.5 h-3.5 rtl-flip" />
                          تراجع
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent dir="rtl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>تراجع كامل</AlertDialogTitle>
                          <AlertDialogDescription>
                            سيتم حذف كل المنتجات التي أُنشئت في سلة من هذه الجلسة ({session.progress?.created || 0} منتج). العملية لا يمكن التراجع عنها.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>إلغاء</AlertDialogCancel>
                          <AlertDialogAction onClick={rollback} className="bg-amber-600 hover:bg-amber-700">احذف من سلة</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid="import-clear-button" className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[var(--border-color)] text-[var(--text-muted)] rounded-lg hover:border-red-300 hover:text-red-500 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                        إزالة الجلسة
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent dir="rtl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>إزالة الجلسة</AlertDialogTitle>
                        <AlertDialogDescription>سيتم حذف المعاينة الحالية. لن يؤثر على المنتجات التي أُنشئت في سلة (لحذفها استخدم «تراجع»).</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={clearSession} className="bg-red-500 hover:bg-red-600">نعم، احذف</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  {session.status === "running" ? (
                    <button onClick={cancelRun} data-testid="import-cancel-button"
                      className="flex items-center gap-1.5 px-4 py-2 text-xs bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors">
                      <Square className="w-3.5 h-3.5 fill-current" />
                      إيقاف
                    </button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="import-execute-button" disabled={!session.stats?.products_count}
                          className="flex items-center gap-1.5 px-4 py-2 text-xs bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-40 text-white rounded-lg font-medium transition-colors">
                          <Sparkles className="w-3.5 h-3.5" />
                          إنشاء في سلة
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent dir="rtl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>تأكيد الإنشاء في سلة</AlertDialogTitle>
                          <AlertDialogDescription>
                            سيتم إنشاء <b className="font-mono">{session.stats?.products_count}</b> منتج مع{" "}
                            <b className="font-mono">{session.stats?.variants_count}</b> مقاس في متجرك.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>إلغاء</AlertDialogCancel>
                          <AlertDialogAction onClick={execute} className="bg-[#0044FF] hover:bg-[#0033CC]">نعم، ابدأ الإنشاء</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {session.status === "running" && session.progress && (
                <div className="px-6 py-4 border-b border-[var(--border-muted)] bg-blue-50/50" data-testid="import-progress">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-semibold text-[#0044FF] flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      جارٍ الإنشاء في سلة...
                    </span>
                    <span className="font-mono text-[var(--text-secondary)]">{session.progress.done}/{session.progress.total}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0044FF] to-[#4d84ff] rounded-full transition-all duration-500"
                      style={{ width: `${(session.progress.done / Math.max(1, session.progress.total)) * 100}%` }}
                    />
                  </div>
                  <div className="flex gap-5 text-xs mt-2 font-mono">
                    <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{session.progress.created} نجح</span>
                    <span className="text-red-500 flex items-center gap-1"><XCircle className="w-3 h-3" />{session.progress.failed} فشل</span>
                    <span className="text-[var(--text-muted)]">{session.progress.skipped} تخطّي</span>
                  </div>
                </div>
              )}

              {/* Completion status banner */}
              {session.status && session.status !== "running" && session.progress?.total > 0 && (() => {
                const cfg = statusConfig[session.status] || statusConfig.pending;
                return (
                  <div className={`px-6 py-3 border-b border-[var(--border-muted)] flex flex-wrap items-center gap-4 text-xs ${cfg.bg} border`}>
                    <span className={`font-semibold ${cfg.color}`}>{cfg.label}</span>
                    <span className="font-mono text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{session.progress.created} منتج نجح</span>
                    <span className="font-mono text-red-500 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />{session.progress.failed} فشل</span>
                    <span className="font-mono text-[var(--text-muted)]">{session.progress.skipped} تخطّي</span>
                  </div>
                );
              })()}

              {/* Warnings */}
              {(session.warnings || []).length > 0 && (
                <div className="px-6 py-3 border-b border-[var(--border-muted)] bg-amber-50/50 flex items-start gap-2 text-xs">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="space-y-0.5 text-amber-700">
                    {session.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                </div>
              )}

              {/* Products list */}
              {(session.products || []).length === 0 ? (
                <div className="p-12 text-center text-sm text-[var(--text-muted)]">لا توجد منتجات.</div>
              ) : (
                <div className="divide-y divide-gray-100" data-testid="import-products-list">
                  {/* Search bar */}
                  <div className="px-4 md:px-6 py-3 border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]/60">
                    <div className="relative max-w-sm">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
                      <input
                        data-testid="import-product-search"
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="ابحث باسم المنتج أو SKU أو رقم الموديل..."
                        className="w-full pr-9 pl-3 py-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[#0044FF]/30 focus:border-[#0044FF]/50 transition-all"
                        dir="rtl"
                      />
                      {productSearch && (
                        <button
                          onClick={() => setProductSearch("")}
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                          title="مسح البحث"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {productSearch && (
                      <div className="mt-1.5 text-xs text-[var(--text-muted)] font-mono">
                        {filteredProducts.length} / {(session.products || []).length} منتج
                      </div>
                    )}
                  </div>
                  {filteredProducts.length === 0 && productSearch ? (
                    <div className="p-10 text-center text-sm text-[var(--text-muted)]">
                      لا توجد نتائج لـ "<span className="font-mono text-[var(--text-secondary)]">{productSearch}</span>"
                    </div>
                  ) : (
                  filteredProducts.map((p) => {
                    const errs = (p.errors?.length || 0) + p.variants.reduce((a, v) => a + (v.errors?.length || 0), 0);
                    const isCreated = p.creation_status === "created";
                    const isFailed  = p.creation_status === "failed";
                    return (
                      <div key={p.temp_id} data-testid={`import-product-${p.temp_id}`}
                        className={isCreated ? "bg-emerald-50/30" : isFailed ? "bg-red-50/20" : ""}>
                        {/* Product header row */}
                        <div className="px-4 md:px-6 py-3.5 flex items-center gap-3 hover:bg-[var(--bg-card-hover)]/60 cursor-pointer transition-colors"
                          onClick={() => toggle(p.temp_id)}>
                          <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform shrink-0 ${expanded[p.temp_id] ? "" : "-rotate-90 rtl-flip"}`} />
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            isCreated ? "bg-emerald-100" : isFailed ? "bg-red-100" : "bg-[#0044FF]/10"
                          }`}>
                            {isCreated ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              : isFailed ? <XCircle className="w-4 h-4 text-red-500" />
                              : <Package className="w-4 h-4 text-[#0044FF]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 truncate">
                              {editingCell?.type === "product_name" && editingCell?.temp_id === p.temp_id ? (
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    autoFocus
                                    value={editVal}
                                    onChange={(e) => setEditVal(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={handleEditKeyDown}
                                    className="flex-1 px-2 py-0.5 text-sm border border-[#0044FF]/40 rounded-lg bg-blue-50 focus:outline-none font-semibold"
                                  />
                                  <button onClick={(e) => { e.stopPropagation(); commitEdit(); }}
                                    className="p-1 text-emerald-600 hover:text-emerald-700">
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                    className="p-1 text-[var(--text-muted)] hover:text-red-400">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); startEdit("product_name", p.temp_id, null, p.name || ""); }}
                                  className="group/name flex items-center gap-1.5 hover:text-[#0044FF] transition-colors w-full text-start"
                                  title="اضغط لتعديل الاسم"
                                >
                                  <span>{p.name || <span className="text-[var(--text-muted)] italic text-sm">بدون اسم</span>}</span>
                                  <Pencil className="w-3 h-3 opacity-0 group-hover/name:opacity-50 text-[#0044FF] shrink-0" />
                                </button>
                              )}
                            </div>
                            <div className="text-xs text-[var(--text-muted)] flex gap-3 mt-0.5 flex-wrap">
                              {p.mpn && <span className="font-mono">الموديل: {p.mpn}</span>}
                              <span className="font-mono">{p.variants.length} مقاس</span>
                              {p.requires_shipping === false && (
                                <span className="text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded text-[10px] font-semibold">رقمي · لا يتطلب شحن</span>
                              )}
                              {/* Price mode badge */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePriceMode(p.temp_id, p.price_mode || "variant"); }}
                                data-testid={`price-mode-toggle-${p.temp_id}`}
                                title="اضغط للتبديل بين 'سعر لكل مقاس' و'سعر المنتج'"
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                                  p.price_mode === "product"
                                    ? "bg-violet-50 text-violet-600 border-violet-200"
                                    : "bg-[var(--bg-card-hover)] text-[var(--text-muted)] border-[var(--border-color)] hover:border-gray-400"
                                }`}
                              >
                                {p.price_mode === "product"
                                  ? <><DollarSign className="w-2.5 h-2.5" />سعر المنتج</>
                                  : <><Tag className="w-2.5 h-2.5" />سعر لكل مقاس</>}
                              </button>
                              {/* Product-level price input */}
                              {p.price_mode === "product" && (
                                <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  {editingProductPrice === p.temp_id ? (
                                    <input
                                      autoFocus
                                      type="number"
                                      value={productPriceVal}
                                      onChange={(e) => setProductPriceVal(e.target.value)}
                                      onBlur={() => commitProductPrice(p.temp_id)}
                                      onKeyDown={(e) => { if (e.key === "Enter") commitProductPrice(p.temp_id); if (e.key === "Escape") { setEditingProductPrice(null); setProductPriceVal(""); } }}
                                      className="w-20 px-1.5 py-0.5 text-xs border border-violet-300 rounded bg-violet-50 focus:outline-none font-mono"
                                      data-testid={`product-price-input-${p.temp_id}`}
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => { setEditingProductPrice(p.temp_id); setProductPriceVal(p.product_price != null ? String(p.product_price) : ""); }}
                                      className="font-mono text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded text-[10px] hover:border-violet-400 transition-colors"
                                      data-testid={`product-price-display-${p.temp_id}`}
                                    >
                                      {p.product_price != null ? `${p.product_price.toLocaleString("en-US")} ر.س` : "أدخل السعر..."}
                                    </button>
                                  )}
                                </span>
                              )}
                              {isCreated && (
                                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  أُنشئ · ID {p.salla_product_id}
                                </span>
                              )}
                              {isFailed && (
                                <span className="text-red-500 font-semibold flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  فشل: {p.creation_error}
                                </span>
                              )}
                              {p.creation_status === "skipped" && (
                                <span className="text-[var(--text-muted)] font-semibold">⊘ تخطّي: {p.creation_error}</span>
                              )}
                              {p.creation_status === "deleted" && (
                                <span className="text-amber-600 font-semibold">↺ حُذف من سلة</span>
                              )}
                              {p.errors?.length > 0 && (
                                <span className="text-red-500 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  {p.errors.join(" · ")}
                                </span>
                              )}
                            </div>
                          </div>
                          {errs > 0 && (
                            <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-semibold shrink-0">
                              {errs} خطأ
                            </span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); removeProduct(p.temp_id); }}
                            className="p-1.5 border border-[var(--border-color)] rounded-lg hover:border-red-300 hover:text-red-500 transition-colors shrink-0"
                            data-testid={`import-remove-product-${p.temp_id}`}
                            title="حذف المنتج وكل مقاساته"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Expanded content */}
                        {expanded[p.temp_id] && p.variants.length > 0 && (
                          <div className="bg-[var(--bg-card-hover)]/70 px-3 md:px-6 py-4 border-t border-[var(--border-muted)]">
                            {/* Images section */}
                            <div className="mb-4">
                              {(p.images || []).length > 0 ? (
                                <>
                                  <div className="text-[11px] text-[var(--text-muted)] uppercase tracking-widest mb-2.5 flex items-center gap-2 font-semibold">
                                    <ImageIcon className="w-3 h-3" />
                                    اختر الصورة الرئيسية
                                    {p.images_skip_upload && (
                                      <span className="text-[10px] bg-gray-200 text-[var(--text-secondary)] px-2 py-0.5 rounded-full font-semibold">بدون صورة</span>
                                    )}
                                    <span className="text-[10px] text-[var(--text-muted)] font-normal mr-auto">{p.images.length} / 10 صور</span>
                                  </div>
                                  <div className="flex gap-2 flex-wrap items-end">
                                    {p.images.map((img, imgIdx) => {
                                      const isApproved = p.approved_image_url === img.url;
                                      const hasApproval = !!p.approved_image_url;
                                      const isAuto = !hasApproval && !p.images_skip_upload && imgIdx === 0;
                                      const highlighted = isApproved || isAuto;
                                      return (
                                        <div key={imgIdx} className="relative group flex flex-col items-center gap-1">
                                          <button
                                            onClick={() => approveImage(p.temp_id, isApproved ? null : img.url)}
                                            title={isApproved ? "إلغاء الاختيار" : "اختر هذه الصورة"}
                                            className={`relative block rounded-xl overflow-hidden border-2 transition-all ${
                                              highlighted ? "border-emerald-500 ring-2 ring-emerald-200" : "border-[var(--border-color)] hover:border-[#004EEB]/40 opacity-60 hover:opacity-100"
                                            }`}
                                          >
                                            <img src={img.thumbnail || img.url} alt={img.title || ""}
                                              className="w-16 h-16 object-cover"
                                              onError={(e) => { e.target.style.display = "none"; }} />
                                            {highlighted && (
                                              <div className="absolute inset-0 bg-emerald-500/10 flex items-center justify-center">
                                                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                                                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                                </div>
                                              </div>
                                            )}
                                            {isAuto && !hasApproval && (
                                              <span className="absolute bottom-0 inset-x-0 bg-emerald-500/90 text-white text-[8px] text-center py-0.5 font-bold">تلقائي</span>
                                            )}
                                            {img.validated && !highlighted && (
                                              <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 bg-blue-500 rounded-full flex items-center justify-center" title="صورة متحقق منها">
                                                <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                                              </span>
                                            )}
                                          </button>
                                          <button onClick={() => removeImage(p.temp_id, img.url)}
                                            className="opacity-0 group-hover:opacity-100 text-[9px] text-red-400 hover:text-red-600 transition-opacity">
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                    <button onClick={() => approveImage(p.temp_id, "__skip__")}
                                      title="بدون صورة"
                                      className={`w-16 h-16 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-[10px] transition-all ${
                                        p.images_skip_upload ? "border-red-400 bg-red-50 text-red-500" : "border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-400"
                                      }`}>
                                      <X className="w-4 h-4 mb-0.5" />
                                      بدون صورة
                                    </button>
                                  </div>
                                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <button onClick={() => openImagePicker(p.temp_id, p.name)}
                                      className="h-7 px-3 text-[11px] font-medium border border-[#004EEB]/30 text-[#004EEB] rounded-lg flex items-center gap-1.5 hover:bg-[#004EEB]/5 transition"
                                      data-testid={`import-open-image-picker-${p.temp_id}`}>
                                      <Search className="w-3 h-3" />
                                      بحث يدوي عن صورة
                                    </button>
                                    <button onClick={() => setShowPasteInput((prev) => ({ ...prev, [p.temp_id]: !prev[p.temp_id] }))}
                                      className={`h-7 px-3 text-[11px] font-medium border rounded-lg flex items-center gap-1.5 transition ${showPasteInput[p.temp_id] ? "border-[#004EEB]/40 text-[#004EEB] bg-[#004EEB]/5" : "border-[var(--border-color)] text-[var(--text-muted)] hover:border-[var(--border-color)]"}`}
                                      data-testid={`toggle-paste-urls-${p.temp_id}`}>
                                      <Link2 className="w-3 h-3" />
                                      إضافة رابط
                                    </button>
                                  </div>
                                  {showPasteInput[p.temp_id] && (
                                    <div className="flex gap-1.5 items-start mt-2" data-testid={`paste-urls-panel-${p.temp_id}`}>
                                      <textarea rows={2} value={pasteUrls[p.temp_id] || ""}
                                        onChange={(e) => setPasteUrls((prev) => ({ ...prev, [p.temp_id]: e.target.value }))}
                                        placeholder="الصق روابط الصور مفصولة بفاصلة أو سطر جديد..."
                                        className="flex-1 px-2 py-1.5 text-[11px] border border-[var(--border-color)] rounded-lg focus:outline-none focus:border-[#004EEB]/40 resize-none"
                                        data-testid={`paste-url-input-${p.temp_id}`} />
                                      <button onClick={() => submitPasteUrls(p.temp_id)}
                                        disabled={!pasteUrls[p.temp_id]?.trim() || addingUrls[p.temp_id]}
                                        className="h-[52px] px-3 text-[11px] font-medium bg-[#004EEB] text-white rounded-lg flex flex-col items-center justify-center gap-0.5 hover:bg-[#003bc4] transition disabled:opacity-40"
                                        data-testid={`paste-url-submit-${p.temp_id}`}>
                                        {addingUrls[p.temp_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                        إضافة
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><ImageIcon className="w-3 h-3" />لا توجد صور</span>
                                  <button onClick={() => openImagePicker(p.temp_id, p.name)}
                                    className="h-7 px-3 text-[11px] font-medium border border-[#004EEB]/30 text-[#004EEB] rounded-lg flex items-center gap-1.5 hover:bg-[#004EEB]/5 transition"
                                    data-testid={`import-open-image-picker-${p.temp_id}`}>
                                    <Search className="w-3 h-3" />بحث يدوي عن صورة
                                  </button>
                                  <button onClick={() => setShowPasteInput((prev) => ({ ...prev, [p.temp_id]: !prev[p.temp_id] }))}
                                    className={`h-7 px-3 text-[11px] font-medium border rounded-lg flex items-center gap-1.5 transition ${showPasteInput[p.temp_id] ? "border-[#004EEB]/40 text-[#004EEB] bg-[#004EEB]/5" : "border-[var(--border-color)] text-[var(--text-muted)] hover:border-[var(--border-color)]"}`}
                                    data-testid={`toggle-paste-urls-${p.temp_id}`}>
                                    <Link2 className="w-3 h-3" />إضافة رابط
                                  </button>
                                  {showPasteInput[p.temp_id] && (
                                    <div className="flex gap-1.5 items-start mt-2 w-full" data-testid={`paste-urls-panel-${p.temp_id}`}>
                                      <textarea rows={2} value={pasteUrls[p.temp_id] || ""}
                                        onChange={(e) => setPasteUrls((prev) => ({ ...prev, [p.temp_id]: e.target.value }))}
                                        placeholder="الصق روابط الصور مفصولة بفاصلة أو سطر جديد..."
                                        className="flex-1 px-2 py-1.5 text-[11px] border border-[var(--border-color)] rounded-lg focus:outline-none focus:border-[#004EEB]/40 resize-none"
                                        data-testid={`paste-url-input-${p.temp_id}`} />
                                      <button onClick={() => submitPasteUrls(p.temp_id)}
                                        disabled={!pasteUrls[p.temp_id]?.trim() || addingUrls[p.temp_id]}
                                        className="h-[52px] px-3 text-[11px] font-medium bg-[#004EEB] text-white rounded-lg flex flex-col items-center justify-center gap-0.5 hover:bg-[#003bc4] transition disabled:opacity-40"
                                        data-testid={`paste-url-submit-${p.temp_id}`}>
                                        {addingUrls[p.temp_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                        إضافة
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Variants table */}
                            <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
                              <table className="w-full min-w-[520px] text-sm bg-[var(--bg-card)]">
                                <thead>
                                  <tr className="border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]">
                                    <th className="text-start px-4 py-2.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">SKU</th>
                                    <th className="text-start px-4 py-2.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">المقاس</th>
                                    <th className="text-end px-4 py-2.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">الكمية</th>
                                    <th className={`text-end px-4 py-2.5 text-[10px] uppercase tracking-widest font-semibold ${p.price_mode === "product" ? "text-gray-200" : "text-[var(--text-muted)]"}`}>
                                      السعر {p.price_mode === "product" && <span className="normal-case">(من المنتج)</span>}
                                    </th>
                                    <th className="text-start px-4 py-2.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">الحالة</th>
                                    <th className="text-center py-2.5 w-16"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.variants.map((v) => (
                                    <tr key={v.temp_id} className="border-t border-[var(--border-muted)] hover:bg-[var(--bg-card-hover)]/60 transition-colors">
                                      <td className="px-4 py-2">
                                        <InlineCell
                                          value={v.sku}
                                          placeholder="SKU"
                                          editing={editingCell?.type === "variant" && editingCell?.temp_id === v.temp_id && editingCell?.field === "sku"}
                                          editVal={editVal}
                                          onChange={setEditVal}
                                          onStart={() => startEdit("variant", v.temp_id, "sku", v.sku)}
                                          onCommit={commitEdit}
                                          onKeyDown={handleEditKeyDown}
                                        />
                                      </td>
                                      <td className="px-4 py-2">
                                        <InlineCell
                                          value={v.size}
                                          placeholder="المقاس"
                                          editing={editingCell?.type === "variant" && editingCell?.temp_id === v.temp_id && editingCell?.field === "size"}
                                          editVal={editVal}
                                          onChange={setEditVal}
                                          onStart={() => startEdit("variant", v.temp_id, "size", v.size)}
                                          onCommit={commitEdit}
                                          onKeyDown={handleEditKeyDown}
                                        />
                                      </td>
                                      <td className="px-4 py-2 text-end">
                                        <InlineCell
                                          value={v.qty != null ? String(v.qty) : ""}
                                          placeholder="0"
                                          inputType="number"
                                          editing={editingCell?.type === "variant" && editingCell?.temp_id === v.temp_id && editingCell?.field === "qty"}
                                          editVal={editVal}
                                          onChange={setEditVal}
                                          onStart={() => startEdit("variant", v.temp_id, "qty", v.qty != null ? String(v.qty) : "")}
                                          onCommit={commitEdit}
                                          onKeyDown={handleEditKeyDown}
                                        />
                                      </td>
                                      <td className="px-4 py-2">
                                        <InlineCell
                                          value={v.price != null ? String(v.price) : ""}
                                          placeholder="0.00"
                                          inputType="number"
                                          editing={editingCell?.type === "variant" && editingCell?.temp_id === v.temp_id && editingCell?.field === "price"}
                                          editVal={editVal}
                                          onChange={setEditVal}
                                          onStart={() => p.price_mode !== "product" && startEdit("variant", v.temp_id, "price", v.price != null ? String(v.price) : "")}
                                          onCommit={commitEdit}
                                          onKeyDown={handleEditKeyDown}
                                        />
                                        {p.price_mode === "product" && (
                                          <span className="text-[10px] text-[var(--text-muted)] font-mono">من المنتج</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2.5 text-xs">
                                        {v.errors?.length > 0 ? (
                                          <span className="text-red-500 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" />{v.errors.join(" · ")}
                                          </span>
                                        ) : (
                                          <span className="text-emerald-600 flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" />جاهز
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2.5 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <button
                                            type="button"
                                            onClick={() => openMoveModal(v, p.temp_id)}
                                            title="نقل المقاس لمنتج آخر"
                                            data-testid={`move-variant-btn-${v.temp_id}`}
                                            className="p-1.5 hover:text-[#0044FF] hover:bg-blue-50 text-[var(--text-muted)] transition-colors rounded border border-transparent hover:border-blue-200"
                                          >
                                            <ArrowRightLeft className="w-3.5 h-3.5" />
                                          </button>
                                          <button onClick={() => removeVariant(v.temp_id)}
                                            className="p-1.5 hover:text-red-500 text-[var(--text-muted)] transition-colors rounded"
                                            data-testid={`import-remove-variant-${v.temp_id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}

                                  {/* Add variant row */}
                                  {addingVariant[p.temp_id] ? (
                                    <tr className="border-t border-[#0044FF]/20 bg-blue-50/40">
                                      <td className="px-4 py-2">
                                        <input type="text" placeholder="SKU"
                                          value={newVariant[p.temp_id]?.sku || ""}
                                          onChange={(e) => setNewVariant((v) => ({ ...v, [p.temp_id]: { ...v[p.temp_id], sku: e.target.value } }))}
                                          className="w-full px-1.5 py-0.5 text-xs border border-[#0044FF]/30 rounded bg-[var(--bg-card)] focus:outline-none font-mono"
                                          data-testid={`new-variant-sku-${p.temp_id}`}
                                        />
                                      </td>
                                      <td className="px-4 py-2">
                                        <input type="text" placeholder="المقاس (مثل: 42)"
                                          value={newVariant[p.temp_id]?.size || ""}
                                          onChange={(e) => setNewVariant((v) => ({ ...v, [p.temp_id]: { ...v[p.temp_id], size: e.target.value } }))}
                                          className="w-full px-1.5 py-0.5 text-xs border border-[#0044FF]/30 rounded bg-[var(--bg-card)] focus:outline-none"
                                          data-testid={`new-variant-size-${p.temp_id}`}
                                        />
                                      </td>
                                      <td className="px-4 py-2">
                                        <input type="number" placeholder="0"
                                          value={newVariant[p.temp_id]?.qty || ""}
                                          onChange={(e) => setNewVariant((v) => ({ ...v, [p.temp_id]: { ...v[p.temp_id], qty: e.target.value } }))}
                                          className="w-full px-1.5 py-0.5 text-xs border border-[#0044FF]/30 rounded bg-[var(--bg-card)] focus:outline-none font-mono text-end"
                                        />
                                      </td>
                                      <td className="px-4 py-2">
                                        <input type="number" placeholder="0.00"
                                          value={newVariant[p.temp_id]?.price || ""}
                                          onChange={(e) => setNewVariant((v) => ({ ...v, [p.temp_id]: { ...v[p.temp_id], price: e.target.value } }))}
                                          className="w-full px-1.5 py-0.5 text-xs border border-[#0044FF]/30 rounded bg-[var(--bg-card)] focus:outline-none font-mono text-end"
                                        />
                                      </td>
                                      <td className="px-4 py-2" colSpan={2}>
                                        <div className="flex items-center gap-1.5">
                                          <button onClick={() => commitAddVariant(p.temp_id)}
                                            className="flex items-center gap-1 px-2.5 py-1 bg-[#0044FF] text-white text-[11px] rounded-lg font-medium hover:bg-[#0033CC] transition-colors"
                                            data-testid={`confirm-add-variant-${p.temp_id}`}>
                                            <Check className="w-3 h-3" />
                                            إضافة
                                          </button>
                                          <button onClick={() => setAddingVariant((v) => ({ ...v, [p.temp_id]: false }))}
                                            className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ) : (
                                    <tr className="border-t border-dashed border-[var(--border-color)]">
                                      <td colSpan={6} className="px-4 py-2">
                                        <button
                                          onClick={() => startAddVariant(p.temp_id)}
                                          data-testid={`add-variant-btn-${p.temp_id}`}
                                          className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] hover:text-[#0044FF] transition-colors font-medium"
                                        >
                                          <Plus className="w-3.5 h-3.5" />
                                          إضافة مقاس جديد
                                        </button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                  )}
                </div>
              )}
            </section>
          </>
        )}

        {/* ══ Image Picker Dialog ══════════════════════════════════════════ */}
        <Dialog open={imgPickerState.open} onOpenChange={(open) => !open && setImgPickerState((s) => ({ ...s, open: false }))}>
          <DialogContent className="max-w-3xl w-full" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold">
                اختر صور المنتج
                {imgPickerState.productName && (
                  <span className="text-[var(--text-muted)] font-normal text-sm mr-2">— {imgPickerState.productName}</span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
                <input type="text" dir="rtl" autoFocus
                  placeholder="ابحث عن صورة... مثال: Nike Air Max 270"
                  value={imgPickerState.query}
                  onChange={(e) => setImgPickerState((s) => ({ ...s, query: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && runPickerSearch(imgPickerState.query)}
                  className="w-full h-9 pr-10 pl-3 text-sm border border-[var(--border-color)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004EEB]/30 focus:border-[#004EEB] bg-[var(--bg-card)]"
                  data-testid="image-picker-search-input" />
              </div>
              <button onClick={() => runPickerSearch(imgPickerState.query)}
                disabled={!imgPickerState.query.trim() || imgPickerState.loading}
                className="h-9 px-4 bg-[#004EEB] text-white text-sm font-medium rounded-lg disabled:opacity-40 flex items-center gap-1.5 hover:bg-[#0039cc] transition"
                data-testid="image-picker-search-btn">
                {imgPickerState.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                بحث
              </button>
            </div>
            {imgPickerState.selected.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-[#004EEB] font-medium">
                <CheckCircle2 className="w-4 h-4" />
                تم تحديد {imgPickerState.selected.length} صورة
                <span className="text-[var(--text-muted)] font-normal">(حد أقصى 10)</span>
              </div>
            )}
            {imgPickerState.loading && (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-[var(--text-muted)]">
                <Loader2 className="w-8 h-8 animate-spin text-[#004EEB]" />
                <span className="text-sm">جارٍ البحث والتحقق من الصور...</span>
              </div>
            )}
            {!imgPickerState.loading && imgPickerState.results.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-[var(--text-muted)] gap-2">
                <ImageIcon className="w-10 h-10 opacity-30" />
                <p className="text-sm">اكتب مصطلح البحث واضغط بحث</p>
              </div>
            )}
            {!imgPickerState.loading && imgPickerState.results.length > 0 && (
              <div className="grid grid-cols-5 gap-2 max-h-96 overflow-y-auto pr-1" data-testid="image-picker-grid">
                {imgPickerState.results.map((img, i) => {
                  const isSelected = imgPickerState.selected.some((x) => x.url === img.url);
                  return (
                    <button key={i} onClick={() => togglePickerSelect(img)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square ${
                        isSelected ? "border-[#004EEB] ring-2 ring-[#004EEB]/30 shadow-md" : "border-[var(--border-color)] hover:border-[#004EEB]/40 opacity-80 hover:opacity-100"
                      }`}
                      title={img.title || ""}
                      data-testid={`image-picker-item-${i}`}>
                      <img src={img.thumbnail || img.url} alt={img.title || ""}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }} />
                      {isSelected && (
                        <div className="absolute inset-0 bg-[#004EEB]/10 flex items-center justify-center">
                          <div className="w-6 h-6 bg-[#004EEB] rounded-full flex items-center justify-center shadow-lg">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                      {img.validated && !isSelected && (
                        <span className="absolute top-1 right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center shadow" title="متحقق منها">
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <DialogFooter className="mt-2 gap-2 flex-row-reverse sm:flex-row-reverse">
              <button onClick={confirmPickerSelection} disabled={!imgPickerState.selected.length}
                className="h-9 px-5 bg-[#004EEB] text-white text-sm font-medium rounded-lg disabled:opacity-40 flex items-center gap-2 hover:bg-[#0039cc] transition"
                data-testid="image-picker-confirm-btn">
                <Plus className="w-4 h-4" />
                إضافة {imgPickerState.selected.length > 0 ? `${imgPickerState.selected.length} صورة` : "الصور المحددة"}
              </button>
              <button onClick={() => setImgPickerState((s) => ({ ...s, open: false }))}
                className="h-9 px-4 border border-[var(--border-color)] text-[var(--text-secondary)] text-sm rounded-lg hover:bg-[var(--bg-card-hover)] transition"
                data-testid="image-picker-cancel-btn">
                إلغاء
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ══ Move Variant Modal ══════════════════════════════════════════ */}
        {moveState && session && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setMoveState(null)}>
            <div className="bg-[var(--bg-card)] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} dir="rtl">
              <div className="px-6 py-5 border-b border-[var(--border-muted)]">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-[#0044FF]" />
                  نقل المقاس إلى منتج آخر
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  المقاس: <span className="font-mono font-semibold text-[var(--text-secondary)]">{moveState.variantLabel}</span>
                </p>
              </div>
              <div className="px-4 py-3 max-h-72 overflow-y-auto space-y-0.5">
                {(session.products || [])
                  .filter((p) => p.temp_id !== moveState.srcProductId)
                  .map((p) => (
                    <button
                      key={p.temp_id}
                      onClick={() => confirmMove(p.temp_id)}
                      data-testid={`move-to-product-${p.temp_id}`}
                      className="w-full text-start px-3 py-3 hover:bg-blue-50 rounded-xl transition-colors"
                    >
                      <div className="font-medium text-gray-800 text-sm">{p.name || "بدون اسم"}</div>
                      <div className="text-[11px] text-[var(--text-muted)] flex gap-2 mt-0.5">
                        {p.mpn && <span className="font-mono">الموديل: {p.mpn}</span>}
                        <span>{p.variants?.length || 0} مقاس</span>
                      </div>
                    </button>
                  ))}
                {(session.products || []).filter((p) => p.temp_id !== moveState.srcProductId).length === 0 && (
                  <p className="text-sm text-[var(--text-muted)] text-center py-6">لا توجد منتجات أخرى في الجلسة.</p>
                )}
              </div>
              <div className="px-6 py-4 border-t border-[var(--border-muted)]">
                <button onClick={() => setMoveState(null)} className="w-full py-2 border border-[var(--border-color)] rounded-xl text-sm text-[var(--text-muted)] hover:border-gray-400 transition-colors">
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        </>)}
      </div>
    </div>
  );
}
